/**
 * Expression Routing — Maps math expressions to the best matching exploration page.
 *
 * Self-contained module with zero gmath dependencies. All pattern detection
 * uses regex only, so this can run on the index page (which doesn't load gmath).
 *
 * @module expression-routing
 */

// ── LaTeX normalizer ──────────────────────────────────────────────────────────
// Converts common Mathpix OCR LaTeX output into a rough ASCII form suitable
// for regex-based pattern matching. Not a full converter — just enough for routing.

/**
 * Normalize LaTeX into ASCII-ish form for pattern matching.
 * @param {string} latex
 * @returns {string}
 */
export function normalizeLatex(latex) {
  return latex
    .replace(/\\frac\s*\{([^}]+)\}\s*\{([^}]+)\}/g, '($1)/($2)')
    .replace(/\\sqrt\s*\{([^}]+)\}/g, 'sqrt($1)')
    .replace(/\\cdot/g, '*')
    .replace(/\\times/g, '*')
    .replace(/\\div/g, '/')
    .replace(/\\pm/g, '+-')
    .replace(/\\mp/g, '-+')
    .replace(/\\left\s*|\\right\s*/g, '')
    .replace(/\\[({)]/g, (match) => match.slice(1)) // \( → (, \{ → {
    .replace(/\\[)}]/g, (match) => match.slice(1))
    .replace(/\{([^{}]+)\}/g, '$1') // strip remaining single-level braces
    .replace(/\{([^{}]+)\}/g, '$1') // second pass for nested
    .replace(/\\geq?/g, '>=')
    .replace(/\\leq?/g, '<=')
    .replace(/\\neq/g, '!=')
    .replace(/\\(?:sin|cos|tan|log|ln)\b/g, (match) => match.slice(1)) // \sin → sin
    .replace(/\\operatorname\{([^}]+)\}/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Pattern detectors ─────────────────────────────────────────────────────────
// All regex-based, no external dependencies.

function hasEquals(s) {
  return /[^!<>]=/.test(s);
}

function hasVariable(s) {
  return /[a-zA-Z]/.test(s.replace(/sin|cos|tan|log|ln|sqrt/gi, ''));
}

/**
 * Check if an equation is non-linear (contains logs, exponentials with variable
 * exponents, trig functions, etc.). Linear equation matchers should reject these.
 */
function isNonLinear(s) {
  // Contains logarithmic functions
  if (/log|ln\b/i.test(s)) return true;
  // Contains trig functions
  if (/\b(sin|cos|tan|sec|csc|cot)\b/i.test(s)) return true;
  // Contains sqrt/radical
  if (/sqrt|√/.test(s)) return true;
  // Variable in an exponent: number^(expression-with-variable)
  // e.g., 2^x, 9^(2y), e^(3x-4). But NOT x^2 (quadratic)
  if (/\w\s*\^\s*\(?[^)=]*[a-zA-Z]/i.test(s)) return true;
  return false;
}

/** Quadratic: has x^2 (or similar) in an equation */
function isQuadraticEquation(s) {
  if (!hasEquals(s)) return false;
  return /[a-zA-Z]\s*\^\s*2/.test(s) || /[a-zA-Z]\s*\*\*\s*2/.test(s);
}

/** Multi-step equation: variables on both sides, or distribution in equation.
 *  Only for LINEAR equations — rejects logs, exponentials, trig. */
function isMultiStepEquation(s) {
  if (!hasEquals(s)) return false;
  if (isNonLinear(s)) return false;
  const [lhs, rhs] = s.split(/(?<!=)=(?!=)/);
  if (!lhs || !rhs) return false;
  // Variables on both sides
  if (hasVariable(lhs) && hasVariable(rhs)) return true;
  // Distribution in equation: a(x+b) = c
  if (/\d\s*\([^)]*[a-zA-Z]/.test(lhs) || /\d\s*\([^)]*[a-zA-Z]/.test(rhs)) return true;
  return false;
}

/** Two-step equation: ax+b=c or ax-b=c pattern.
 *  Only for LINEAR equations — rejects logs, exponentials, trig. */
function isTwoStepEquation(s) {
  if (!hasEquals(s)) return false;
  if (isNonLinear(s)) return false;
  if (!hasVariable(s)) return false;
  const [lhs, rhs] = s.split(/(?<!=)=(?!=)/);
  if (!lhs || !rhs) return false;
  // Variable side has both multiplication (coefficient) and addition/subtraction
  const varSide = hasVariable(lhs) ? lhs : rhs;
  const hasCoeff = /\d\s*[a-zA-Z]|\d\s*\*\s*[a-zA-Z]/.test(varSide);
  const hasAddSub = /[+-]/.test(varSide.replace(/^\s*-/, '')); // ignore leading negative
  return hasCoeff && hasAddSub;
}

/** One-step equation: x+a=b, x-a=b, ax=b, x/a=b (single operation only).
 *  Only for LINEAR equations — rejects logs, exponentials, trig. */
function isOneStepEquation(s) {
  if (!hasEquals(s)) return false;
  if (isNonLinear(s)) return false;
  if (!hasVariable(s)) return false;
  const [lhs, rhs] = s.split(/(?<!=)=(?!=)/);
  if (!lhs || !rhs) return false;
  const varSide = hasVariable(lhs) ? lhs : rhs;
  const constSide = varSide === lhs ? rhs : lhs;
  // Variable side should have just one operation, not both coefficient and add/sub
  const hasCoeff = /\d\s*[a-zA-Z]|\d\s*\*\s*[a-zA-Z]/.test(varSide);
  const hasAddSub = /[+-]/.test(varSide.replace(/^\s*-/, ''));
  if (hasCoeff && hasAddSub) return false; // That's two-step
  // Constant side should not have a variable (that's multi-step)
  if (hasVariable(constSide)) return false;
  return true;
}

/** FOIL pattern: (a+b)(c+d) */
function hasFoilPattern(s) {
  if (isNonLinear(s)) return false;
  return /\([^)]*[a-zA-Z][^)]*[+-][^)]*\)\s*\*?\s*\([^)]*[a-zA-Z][^)]*[+-][^)]*\)/.test(
    s
  );
}

/** Distributable: a(b+c) or (b+c)a. Rejects log/trig/exponential expressions. */
function hasDistributablePattern(s) {
  if (isNonLinear(s)) return false;
  return (
    /\d\s*\([^)]+[+-][^)]+\)/.test(s) ||
    /[a-zA-Z]\s*\([^)]+[+-][^)]+\)/.test(s) ||
    /\([^)]+[+-][^)]+\)\s*\d/.test(s)
  );
}

/** Factorable by GCF: multiple terms with a common factor (simplified check) */
function hasFactorablePattern(s) {
  if (hasEquals(s)) return false;
  // Look for polynomial with numeric coefficients that share a factor
  const terms = s
    .split(/(?<!\^)[+-]/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (terms.length < 2) return false;
  const coeffs = terms.map((t) => {
    const m = t.match(/^-?\s*(\d+)/);
    return m ? Math.abs(parseInt(m[1], 10)) : 1;
  });
  if (coeffs.some((c) => c <= 1)) return false;
  const g = coeffs.reduce((a, b) => gcd(a, b));
  return g > 1;
}

function gcd(a, b) {
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

/** Exponent patterns: x^n*x^m, (x^n)^m, x^n/x^m */
function hasExponentPattern(s) {
  if (hasEquals(s)) return false;
  // Product of powers: x^a * x^b
  if (/[a-zA-Z]\s*\^\s*\d+\s*\*?\s*[a-zA-Z]\s*\^\s*\d+/.test(s)) return true;
  // Power of a power: (x^a)^b
  if (/\([^)]*\^\s*\d+[^)]*\)\s*\^\s*\d+/.test(s)) return true;
  // Quotient of powers: x^a / x^b
  if (/[a-zA-Z]\s*\^\s*\d+\s*\/\s*[a-zA-Z]\s*\^\s*\d+/.test(s)) return true;
  return false;
}

/** Like terms: 3x+2x, 4a-a+5b */
function hasLikeTerms(s) {
  if (hasEquals(s)) return false;
  // Extract variable parts of terms (e.g., "3x" → "x", "2xy" → "xy")
  const termPattern = /[+-]?\s*\d*\s*[a-zA-Z]+(?:\^\d+)?/g;
  const terms = s.match(termPattern);
  if (!terms || terms.length < 2) return false;
  const varParts = terms.map((t) => t.replace(/[+-]?\s*\d*\s*/, '').trim());
  const seen = new Set();
  for (const v of varParts) {
    if (!v) continue;
    if (seen.has(v)) return true;
    seen.add(v);
  }
  return false;
}

// ── Routing table ─────────────────────────────────────────────────────────────

const ROUTING_RULES = [
  // Equations (ordered from specific to general)
  {
    test: isQuadraticEquation,
    slug: 'quadratic-equations',
    label: 'Quadratic Equations',
    file: 'quadratic-equations.html',
  },
  {
    test: isMultiStepEquation,
    slug: 'multi-step-equations',
    label: 'Multi-Step Equations',
    file: 'multi-step-equations.html',
  },
  {
    test: isTwoStepEquation,
    slug: 'two-step-equations',
    label: 'Two-Step Equations',
    file: 'two-step-equations.html',
  },
  {
    test: isOneStepEquation,
    slug: 'one-step-equations',
    label: 'One-Step Equations',
    file: 'one-step-equations.html',
  },

  // Expressions (no = sign)
  {
    test: hasFoilPattern,
    slug: 'distribution',
    label: 'Distribution',
    file: 'distribution.html',
  },
  {
    test: hasDistributablePattern,
    slug: 'distribution',
    label: 'Distribution',
    file: 'distribution.html',
  },
  {
    test: hasFactorablePattern,
    slug: 'factor-polynomials',
    label: 'Factor Polynomials',
    file: 'factor-polynomials.html',
  },
  {
    test: hasExponentPattern,
    slug: 'exponent-rules',
    label: 'Exponent Rules',
    file: 'exponent-rules.html',
  },
  {
    test: hasLikeTerms,
    slug: 'combine-like-terms',
    label: 'Combine Like Terms',
    file: 'combine-like-terms.html',
  },
];

// ── Individual detector exports (used by whiteboard-guidance) ─────────────────

export {
  hasEquals,
  hasVariable,
  isNonLinear,
  isQuadraticEquation,
  isMultiStepEquation,
  isTwoStepEquation,
  isOneStepEquation,
  hasFoilPattern,
  hasDistributablePattern,
  hasFactorablePattern,
  hasExponentPattern,
  hasLikeTerms,
};

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Route an expression to all matching exploration pages.
 *
 * @param {string} input - Expression in ASCII or LaTeX
 * @returns {Array<{ slug: string, label: string, file: string, url: string, mathNewUrl: string }>}
 *   Returns an empty array if no matching topic page is found.
 *   Results are ordered by specificity (most specific first), deduped by slug.
 */
export function routeExpression(input) {
  if (!input || !input.trim()) return [];

  // Normalize LaTeX if it looks like LaTeX (contains backslashes)
  const normalized = input.includes('\\') ? normalizeLatex(input) : input.trim();
  const encoded = encodeURIComponent(input.trim());

  const matches = [];
  const seen = new Set();

  for (const rule of ROUTING_RULES) {
    if (rule.test(normalized) && !seen.has(rule.slug)) {
      seen.add(rule.slug);
      matches.push({
        slug: rule.slug,
        label: rule.label,
        file: rule.file,
        url: `${rule.file}?eq=${encoded}`,
        mathNewUrl: `https://math.new/?eq=${encoded}`,
      });
    }
  }

  return matches;
}
